import React from 'react';
import { Helmet } from 'react-helmet-async';

export const SITE_URL = 'https://jetcoelectric.biz';
export const SITE_NAME = 'Jetco Electrical Contractors';
export const DEFAULT_OG_IMAGE = 'https://customer-assets.emergentagent.com/job_jetco-electrical/artifacts/x1j92naa_Untitled%20design.png';

/**
 * Reusable SEO component that injects per-page <title>, meta tags,
 * canonical URL, Open Graph / Twitter cards, and JSON-LD structured data.
 *
 * Props:
 *   title        - page title (will be appended with brand)
 *   description  - meta description (50-160 chars ideal)
 *   path         - path of the page (e.g. "/services/ev-charger-installation")
 *   image        - hero / share image URL (defaults to brand logo)
 *   noindex      - if true, adds robots="noindex,nofollow"
 *   keywords     - comma-separated keywords string
 *   jsonLd       - array of JSON-LD objects to inject
 */
export const SEO = ({
  title,
  description,
  path = '/',
  image = DEFAULT_OG_IMAGE,
  noindex = false,
  keywords,
  jsonLd = [],
}) => {
  const canonical = `${SITE_URL}${path}`;
  const fullTitle = title?.includes(SITE_NAME)
    ? title
    : `${title} | ${SITE_NAME}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD */}
      {jsonLd.map((schema, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
